import { ref, watch, computed } from '@vue/composition-api';
import usePersistedPerPage from '@/libs/utils/perPage';
import { title } from '@core/utils/filter';

// Notification
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import store from '@/store';
import parsePhoneNumber from 'libphonenumber-js';
import useProfiles from '@/views/apps/roster/profile/useProfiles';

export default function useStudents() {
  // Use toast
  const toast = useToast();

  const refStudentListTable = ref(null);

  // Table Handlers
  const tableColumns = [
    { key: 'fullName', sortable: true },
    { key: 'email', sortable: true },
    { key: 'phoneNumber', label: 'Phone Number', sortable: true },
    // {
    //   key: 'currentPlan',
    //   label: 'Plan',
    //   formatter: title,
    //   sortable: true,
    // },
    { key: 'status', sortable: true },
    { key: 'actions' },
  ];
  const perPage = usePersistedPerPage('students', 10);
  const totalStudents = ref(0);
  const currentPage = ref(1);
  const totalPageCount = ref(0);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref('');
  const sortBy = ref('id');
  const isSortDirDesc = ref(true);
  const roleFilter = ref(null);
  const statusFilter = ref(null);
  const verificationFilter = ref(null);
  const deraStatusFilter = ref(null);

  const dataMeta = computed(() => {
    const localItemsCount = refStudentListTable.value ? refStudentListTable.value.localItems.length : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalStudents.value,
    };
  });

  const refetchData = () => {
    refStudentListTable.value.refresh();
  };

  watch([currentPage, perPage, searchQuery, roleFilter, statusFilter, verificationFilter, deraStatusFilter], () => {
    refetchData();
  });

  const fetchStudents = (ctx, callback) => {
    const sbv = sortBy.value === 'id' ? '_id' : sortBy.value;
    const sb = `${sbv}:${isSortDirDesc.value ? 'desc' : 'asc'}`;

    store
      .dispatch('studentStoreModule/fetchStudents', {
        search: searchQuery.value,
        limit: perPage.value,
        // sortDesc: isSortDirDesc.value,
        page: currentPage.value,
        sortBy: sb,

        role: roleFilter.value,
        verification: verificationFilter.value,
        status: statusFilter.value,
        deraStatus: deraStatusFilter.value,
      })
      .then((response) => {
        const { results, totalResults, totalPages } = response.data;

        callback(results);
        totalStudents.value = totalResults;
        totalPageCount.value = totalPages;
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching students list-pending',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      });
  };

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveStudentPhoneDetails = (phoneNumber) => {
    return parsePhoneNumber(phoneNumber, 'US') || { country: undefined, formatNational: () => 'Invalid Phone Number' };
  };

  const resolveStudentStatusVariant = (status) => {
    if (status === 'created') return 'secondary';
    if (status === 'enrolling') return 'warning';
    if (status === 'enrolled') return 'info';
    if (status === 'invited') return 'info';
    if (status === 'onboarded') return 'success';
    if (status === 'in_class') return 'success';
    if (status === 'placed') return 'danger';
    return 'info';
  };

  const resolveStudentStatusIcon = (status) => {
    if (status === 'created') return '';
    if (status === 'enrolling') return 'ClockIcon';
    if (status === 'enrolling') return 'CheckCircleIcon';
    if (status === 'in_class') return 'GlobeIcon';
    if (status === 'placed') return '';
    return '';
  };

  const titleOptions = [
    { label: 'Ms.', value: 'Ms' },
    { label: 'Mrs.', value: 'Mrs' },
    { label: 'Mr.', value: 'Mr' },
    { label: 'Dr.', value: 'Dr' },
  ];

  const statusOptions = [
    { label: 'Created', value: 'created' },
    // { label: 'Enrolling', value: 'enrolling' },
    // { label: 'Enrolled', value: 'enrolled' },
    { label: 'Invited', value: 'invited' },
    { label: 'Onboarded', value: 'onboarded' },
    // { label: 'In-Class', value: 'in_class' },
    // { label: 'Placed', value: 'placed' },
    { label: 'Archived', value: 'archived' },
  ];

  const clearanceOptions = [
    { label: 'None', value: 'none' },
    { label: 'Public Trust', value: 'public_trust' },
    { label: 'Confidential', value: 'confidential' },
    { label: 'Secret', value: 'secret' },
    { label: 'Top Secret', value: 'top_secret' },
    { label: 'TS + SCI', value: 'ts_sci' },
    { label: 'TS + POLY', value: 'ts_poly' },
    { label: 'TS + POLY + SCI', value: 'ts_poly_sci' },
  ];

  const relocatableOptions = [
    { label: 'No', value: 'no' },
    { label: 'Conditionally', value: 'conditionally' },
    { label: 'Yes', value: 'yes' },
  ];

  const remoteOptions = [
    { label: 'No', value: 'no' },
    { label: 'Conditionally', value: 'conditionally' },
    { label: 'Yes', value: 'yes' },
  ];

  const verificationOptions = [
    { label: 'Unverified', value: 'unverified' },
    { label: 'Requested', value: 'requested' },
    { label: 'Processing', value: 'processing' },
    { label: 'Action Required', value: 'action_required' },
    { label: 'Verified', value: 'verified' },
  ];

  const { fundingSourceOptions } = useProfiles();

  return {
    fetchStudents,
    tableColumns,
    perPage,
    currentPage,
    totalPageCount,
    totalStudents,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refStudentListTable,

    resolveStudentStatusVariant,
    resolveStudentStatusIcon,

    resolveStudentPhoneDetails,
    refetchData,

    titleOptions,
    statusOptions,
    clearanceOptions,
    relocatableOptions,
    remoteOptions,
    fundingSourceOptions,
    verificationOptions,

    // Extra Filters
    roleFilter,
    statusFilter,
    verificationFilter,
    deraStatusFilter,
  };
}
